import { BALANCE, DIFFICULTY } from '../balance.ts';
import { edgeBetween, getMap, roomDef } from '../maps/graph.ts';
import type { MapDef } from '../maps/types.ts';
import { applyProPick } from '../puzzles/lock.ts';
import { PUZZLES, createPuzzle } from '../puzzles/registry.ts';
import type { ApplyOk, TickOk } from '../puzzles/types.ts';
import { Rng } from '../rng.ts';
import type {
  ActionError,
  ExecState,
  GameConfig,
  GearId,
  PlayerId,
  Plan,
  PuzzleAct,
  PuzzleInstance,
  PuzzleType,
  RoomId,
  Stop,
} from '../types.ts';
import { emit, fail, mustPlayer, type Ctx } from './context.ts';
import {
  actContext,
  activePuzzleIds,
  activePuzzles,
  addAlarm,
  currentStop,
  failHeist,
  genContext,
  heistOver,
  isLooped,
  isTunnel,
  newPuzzleId,
  setPhase,
  tickContext,
} from './core.ts';
import { startHideout } from './hideout.ts';
import { processTimed, scheduleEvents } from './infiltrator.ts';

// ---- Start -----------------------------------------------------------------------------

/** The execution timer before the entrance's own cost (GDD §9). */
export function nominalTimer(cfg: GameConfig, map: MapDef): number {
  if (cfg.timerMs !== undefined) return cfg.timerMs;
  const base = DIFFICULTY[cfg.difficulty].timerMs;
  const byMap = (base * map.timerPermille) / 1000;
  const byPlayers = (byMap * (BALANCE.playerCountTimerPermille[cfg.players.length] ?? 1000)) / 1000;
  return Math.round(byPlayers);
}

function nominalTimerMs(ctx: Ctx, map: MapDef): number {
  return nominalTimer(ctx.s.config, map);
}

function buildItinerary(map: MapDef, plan: Plan): Stop[] {
  const stops: Stop[] = [];
  const route = plan.route;
  route.forEach((room, i) => {
    stops.push({
      kind: 'room',
      room,
      optional: false,
      skipped: false,
      puzzles: [],
      started: false,
      vaultNext: room === map.vault,
    });
    for (const d of plan.detours.filter((x) => x.anchor === room)) {
      if (edgeBetween(map, room, d.room)?.guarded) {
        stops.push({
          kind: 'corridor',
          from: room,
          to: d.room,
          optional: true,
          skipped: false,
          puzzle: null,
        });
      }
      stops.push({
        kind: 'room',
        room: d.room,
        optional: true,
        skipped: false,
        puzzles: [],
        started: false,
        vaultNext: false,
      });
      stops.push({ kind: 'return', from: d.room, to: room, skipped: false });
    }
    const next = route[i + 1];
    if (next) {
      const e = edgeBetween(map, room, next);
      if (!e) stops.push({ kind: 'rope', from: room, to: next });
      else if (e.guarded) {
        stops.push({
          kind: 'corridor',
          from: room,
          to: next,
          optional: false,
          skipped: false,
          puzzle: null,
        });
      }
    }
  });
  return stops;
}

export function startExecution(ctx: Ctx, plan: Plan): void {
  const s = ctx.s;
  const map = getMap(s.config.mapId);
  setPhase(ctx, 'execution');

  let timerMs = nominalTimerMs(ctx, map);
  if (plan.entrance === 'roof') timerMs = Math.max(30_000, timerMs - BALANCE.roofTimerCostMs);
  s.alarm = plan.entrance === 'service' ? BALANCE.fixed.serviceEntrance : 0;

  const exec: ExecState = {
    entrance: plan.entrance,
    entranceLevelAdjust: plan.entrance === 'sewer' ? 1 : plan.entrance === 'service' ? -1 : 0,
    plan: structuredClone(plan),
    timerUs: timerMs * 1000,
    elapsedMs: 0,
    rushUntilMs: 0,
    loopUntilMs: 0,
    droneUntilMs: 0,
    tunnelUntilMs: 0,
    nextTunnelAtMs: map.tunnels ? BALANCE.tunnelEveryMs : -1,
    policeAlerted: s.alarm >= BALANCE.policeAlertAt,
    optionalClosed: false,
    itinerary: buildItinerary(map, plan),
    stopIndex: 0,
    puzzles: {},
    gearLeft: plan.gear.filter((g) => g !== 'rope'),
    disguiseUsed: false,
    proPickUsed: false,
    completedOptional: [],
    lastError: null,
    scheduled: [],
    pending: [],
    lastNoticeAtMs: -1_000_000,
    dripAccMs: 0,
    escapePuzzle: null,
    alarmFinal: null,
    puzzleCounter: 0,
  };
  s.exec = exec;
  emit(ctx, 'exec.started', {
    timerMs,
    entrance: plan.entrance,
    alarm: s.alarm,
    stops: exec.itinerary.map((st) => ({ kind: st.kind, room: 'room' in st ? st.room : null })),
  });
  scheduleEvents(ctx);
  activateStop(ctx, 0);
}

// ---- Stops and puzzles -----------------------------------------------------------------

function isOptionalStop(st: Stop): boolean {
  return (st.kind === 'room' || st.kind === 'corridor') && st.optional;
}

function pickLevel(ctx: Ctx, rng: Rng): number {
  const exec = ctx.s.exec as ExecState;
  const d = DIFFICULTY[ctx.s.config.difficulty];
  const base = rng.int(d.levelMin, d.levelMax) + exec.entranceLevelAdjust;
  exec.entranceLevelAdjust = 0;
  return Math.min(5, Math.max(1, base));
}

function addPuzzle(ctx: Ctx, type: PuzzleType, room: RoomId, label: string): PuzzleInstance {
  const s = ctx.s;
  const exec = s.exec as ExecState;
  const rng = Rng.create(`${s.seed}/puzzle/${label}`);
  const level = pickLevel(ctx, rng.fork('level'));
  const id = newPuzzleId(exec);
  const p = createPuzzle(id, type, genContext(s, rng, level, room));
  exec.puzzles[id] = p;
  emit(ctx, 'puzzle.created', { puzzleId: id, type, room, level });
  return p;
}

/** 1 or 2 puzzle types for a room, preferring a second puzzle for a different role. */
function chooseRoomTypes(rng: Rng, pool: readonly PuzzleType[], count: number): PuzzleType[] {
  const solverOf: Record<string, string> = {
    lock: 'locksmith',
    safe: 'locksmith',
    laser: 'locksmith',
    panel: 'hacker',
    cameras: 'hacker',
    password: 'hacker',
  };
  const first = rng.pick(pool);
  if (count === 1) return [first];
  const others = pool.filter((t) => t !== first);
  const complementary = others.filter((t) => solverOf[t] !== solverOf[first]);
  const second = rng.pick(
    complementary.length > 0 ? complementary : others.length > 0 ? others : [first],
  );
  return [first, second];
}

function activateRoom(ctx: Ctx, stop: Extract<Stop, { kind: 'room' }>): void {
  const s = ctx.s;
  const exec = s.exec as ExecState;
  const map = getMap(s.config.mapId);
  const def = roomDef(map, stop.room);
  const rng = Rng.create(`${s.seed}/room/${stop.room}`);
  stop.started = true;
  emit(ctx, 'stop.room', { room: stop.room, optional: stop.optional });

  if (stop.room === map.vault) {
    if (rng.chance(BALANCE.vaultPrePuzzlePermille)) {
      const type = rng.pick(def.pool);
      stop.puzzles.push(addPuzzle(ctx, type, stop.room, `${stop.room}/pre`).id);
    } else {
      stop.vaultNext = false;
      stop.puzzles.push(addPuzzle(ctx, 'vault', stop.room, `${stop.room}/vault`).id);
    }
    return;
  }
  const two = rng.chance(BALANCE.puzzlesPerRoomTwoPermille + map.secondPuzzleBonusPermille);
  const types = chooseRoomTypes(rng, def.pool, two ? 2 : 1);
  types.forEach((type, slot) => {
    stop.puzzles.push(addPuzzle(ctx, type, stop.room, `${stop.room}/${slot}`).id);
  });
  void exec;
}

function activateCorridor(
  ctx: Ctx,
  stop: Extract<Stop, { kind: 'corridor' }>,
  index: number,
): void {
  const s = ctx.s;
  emit(ctx, 'stop.corridor', { from: stop.from, to: stop.to, optional: stop.optional });
  stop.puzzle = addPuzzle(ctx, 'corridor', stop.to, `corridor/${index}`).id;
  void s;
}

/** Skips the stops of one detour starting at `from` (corridor?, alcove, return). */
function skipDetour(ctx: Ctx, from: number): void {
  const exec = ctx.s.exec as ExecState;
  for (let j = from; j < exec.itinerary.length; j++) {
    const st = exec.itinerary[j] as Stop;
    if (st.kind === 'corridor' && st.optional) st.skipped = true;
    else if (st.kind === 'room' && st.optional) st.skipped = true;
    else if (st.kind === 'return') {
      st.skipped = true;
      return;
    } else return;
  }
}

/** Moves to the stop at `index` (skipping closed or skipped alcoves) and starts it. */
function activateStop(ctx: Ctx, from: number): void {
  const s = ctx.s;
  const exec = s.exec as ExecState;
  let index = from;
  for (;;) {
    if (index >= exec.itinerary.length) {
      startEscape(ctx);
      return;
    }
    exec.stopIndex = index;
    const stop = exec.itinerary[index] as Stop;

    if (stop.kind === 'rope') {
      exec.timerUs -= BALANCE.ropeHopMs * 1000;
      emit(ctx, 'stop.rope', { from: stop.from, to: stop.to });
      index++;
      continue;
    }
    if (stop.kind === 'return') {
      if (!stop.skipped) {
        exec.timerUs -= BALANCE.detourReturnMs * 1000;
        emit(ctx, 'stop.return', { from: stop.from, to: stop.to });
      }
      index++;
      continue;
    }
    if (isOptionalStop(stop) && !('started' in stop && stop.started)) {
      if (exec.optionalClosed && !stop.skipped) skipDetour(ctx, index);
      if (stop.skipped) {
        emit(ctx, 'stop.skipped', { room: stop.kind === 'room' ? stop.room : stop.to });
        index++;
        continue;
      }
    }
    if (stop.kind === 'corridor') {
      activateCorridor(ctx, stop, index);
      return;
    }
    if (stop.kind === 'room') {
      activateRoom(ctx, stop);
      return;
    }
  }
}

function startEscape(ctx: Ctx): void {
  const s = ctx.s;
  const exec = s.exec as ExecState;
  setPhase(ctx, 'escape');
  const map = getMap(s.config.mapId);
  const id = newPuzzleId(exec);
  const rng = Rng.create(`${s.seed}/puzzle/escape`);
  const level = pickLevel(ctx, rng.fork('level'));
  const p = createPuzzle(id, 'escape', genContext(s, rng, level, map.vault));
  exec.puzzles[id] = p;
  exec.escapePuzzle = id;
  emit(ctx, 'puzzle.created', { puzzleId: id, type: 'escape', room: map.vault, level });
  emit(ctx, 'escape.started', { alarm: s.alarm });
}

function markSolved(ctx: Ctx, p: PuzzleInstance): void {
  const s = ctx.s;
  const exec = s.exec as ExecState;
  if (p.solved) return;
  p.solved = true;
  p.solvedAtMs = exec.elapsedMs;
  emit(ctx, 'puzzle.solved', { puzzleId: p.id, type: p.type, room: p.room });
  afterSolve(ctx);
}

function afterSolve(ctx: Ctx): void {
  const s = ctx.s;
  const exec = s.exec as ExecState;
  if (s.phase === 'escape') {
    const esc = exec.escapePuzzle ? exec.puzzles[exec.escapePuzzle] : undefined;
    if (esc?.solved) {
      exec.alarmFinal = s.alarm;
      emit(ctx, 'escape.done', { alarm: s.alarm });
      startHideout(ctx);
    }
    return;
  }
  const stop = currentStop(exec);
  if (!stop) return;
  if (stop.kind === 'room') {
    const all = stop.puzzles.every((id) => (exec.puzzles[id] as PuzzleInstance).solved);
    if (!all) return;
    if (stop.vaultNext) {
      stop.vaultNext = false;
      stop.puzzles.push(addPuzzle(ctx, 'vault', stop.room, `${stop.room}/vault`).id);
      return;
    }
    if (stop.optional) exec.completedOptional.push(stop.room);
    emit(ctx, 'room.cleared', { room: stop.room, optional: stop.optional });
    activateStop(ctx, exec.stopIndex + 1);
  } else if (stop.kind === 'corridor' && stop.puzzle) {
    if ((exec.puzzles[stop.puzzle] as PuzzleInstance).solved) {
      emit(ctx, 'corridor.cleared', { from: stop.from, to: stop.to });
      activateStop(ctx, exec.stopIndex + 1);
    }
  }
}

// ---- Applying a puzzle result ----------------------------------------------------------

interface Outcome {
  errorTenths: number;
  fixedTenths: number;
  sighted: boolean;
  undo: ApplyOk['undo'];
  events: ApplyOk['events'];
  solved: boolean;
}

function settle(ctx: Ctx, p: PuzzleInstance, r: Outcome): void {
  const s = ctx.s;
  const exec = s.exec as ExecState;
  for (const ev of r.events)
    emit(ctx, ev.type, { ...ev.data, puzzleId: p.id }, ev.audience ?? 'all');

  let errorTenths = r.errorTenths;
  if (r.sighted && exec.gearLeft.includes('disguise') && !exec.disguiseUsed) {
    // The disguise absorbs the first sighting (GDD §5.2).
    exec.disguiseUsed = true;
    exec.gearLeft = exec.gearLeft.filter((g) => g !== 'disguise');
    emit(ctx, 'gear.disguise', {});
    errorTenths = 0;
  }
  if (errorTenths > 0) {
    const added = addAlarm(ctx, errorTenths, true, 'error');
    exec.lastError = {
      tenths: added,
      atMs: exec.elapsedMs,
      puzzleId: p.id,
      undo: r.undo,
      used: false,
    };
  }
  if (r.fixedTenths > 0) addAlarm(ctx, r.fixedTenths, false, 'gadget');
  if (heistOver(s)) return;
  // A third wrong vault code locks the vault for good (GDD §8.9).
  if (p.type === 'vault' && (p.state as { failed: boolean }).failed) {
    failHeist(ctx, 'vault');
    return;
  }
  if (r.solved) markSolved(ctx, p);
}

export function puzzleAct(
  ctx: Ctx,
  by: PlayerId,
  puzzleId: string,
  act: PuzzleAct,
): ActionError | null {
  const s = ctx.s;
  const exec = s.exec;
  if ((s.phase !== 'execution' && s.phase !== 'escape') || !exec) {
    return fail('wrongPhase', 'there is no puzzle to work on');
  }
  const p = exec.puzzles[puzzleId];
  if (!p) return fail('unknownPuzzle', 'no such puzzle');
  if (p.solved) return fail('puzzleSolved', 'already solved');
  if (!activePuzzleIds(s).includes(puzzleId))
    return fail('puzzleNotActive', 'the team is not there');

  const actCtx = actContext(s, by);
  const r = PUZZLES[p.type].apply(p, act, actCtx);
  if (!r.ok) return r.error;
  if (r.consumes) {
    const me = mustPlayer(s, by);
    if (r.consumes === 'coin') me.powers.coins -= 1;
    else me.powers.petards -= 1;
  }
  settle(ctx, p, r);
  return null;
}

// ---- Gear, powers, skipping ------------------------------------------------------------

export function useGear(
  ctx: Ctx,
  by: PlayerId,
  gear: GearId,
  puzzleId?: string,
): ActionError | null {
  const s = ctx.s;
  const exec = s.exec;
  if ((s.phase !== 'execution' && s.phase !== 'escape') || !exec) {
    return fail('wrongPhase', 'gear is for the heist');
  }
  if (!exec.gearLeft.includes(gear)) return fail('noCharges', 'you do not have that');
  const me = mustPlayer(s, by);
  const target = puzzleId ? exec.puzzles[puzzleId] : undefined;
  const active = puzzleId ? activePuzzleIds(s).includes(puzzleId) : false;

  const consume = (): void => {
    exec.gearLeft = exec.gearLeft.filter((g) => g !== gear);
    emit(ctx, 'gear.used', { gear });
  };

  switch (gear) {
    case 'proPick': {
      if (me.role !== 'locksmith') return fail('notAllowed', 'only the locksmith picks locks');
      if (!target || !active || target.type !== 'lock' || target.solved) {
        return fail('invalid', 'pick an active lock');
      }
      if ((target.state as { progress: number }).progress > 0)
        return fail('tooLate', 'the lock is already open');
      consume();
      exec.proPickUsed = true;
      if (applyProPick(target)) markSolved(ctx, target);
      return null;
    }
    case 'jammer': {
      if (me.role !== 'hacker') return fail('notAllowed', 'only the hacker jams cameras');
      if (!target || !active || target.type !== 'cameras' || target.solved) {
        return fail('invalid', 'pick an active camera puzzle');
      }
      consume();
      PUZZLES.cameras.autoSolve?.(target);
      markSolved(ctx, target);
      return null;
    }
    case 'emp': {
      if (me.role !== 'hacker') return fail('notAllowed', 'only the hacker uses the EMP');
      if (!target || !active || target.type !== 'panel' || target.solved) {
        return fail('invalid', 'pick an active panel');
      }
      consume();
      PUZZLES.panel.autoSolve?.(target);
      markSolved(ctx, target);
      return null;
    }
    case 'drone': {
      if (me.role !== 'mastermind')
        return fail('notAllowed', 'only the mastermind flies the drone');
      consume();
      exec.droneUntilMs = exec.elapsedMs + BALANCE.droneMs;
      return null;
    }
    case 'disguise':
    case 'rope':
      return fail('invalid', 'that is used automatically or during planning');
  }
}

export function usePower(ctx: Ctx, by: PlayerId, power: 'planB' | 'loop'): ActionError | null {
  const s = ctx.s;
  const exec = s.exec;
  if ((s.phase !== 'execution' && s.phase !== 'escape') || !exec) {
    return fail('wrongPhase', 'powers are for the heist');
  }
  const me = mustPlayer(s, by);
  if (power === 'loop') {
    if (me.role !== 'hacker') return fail('notAllowed', 'only the hacker can loop the cameras');
    if (me.powers.loops <= 0) return fail('noCharges', 'no loops left');
    me.powers.loops -= 1;
    exec.loopUntilMs = exec.elapsedMs + BALANCE.loopMs;
    emit(ctx, 'power.loop', { until: exec.loopUntilMs });
    return null;
  }
  if (me.role !== 'mastermind') return fail('notAllowed', 'only the mastermind has Plan B');
  if (me.powers.planB <= 0) return fail('noCharges', 'no Plan B left');
  const last = exec.lastError;
  if (!last || last.used || exec.elapsedMs - last.atMs > BALANCE.planBWindowMs) {
    return fail('nothingToDo', 'there is no recent mistake to undo');
  }
  me.powers.planB -= 1;
  last.used = true;
  s.alarm = Math.max(0, s.alarm - last.tenths);
  const p = last.puzzleId ? exec.puzzles[last.puzzleId] : undefined;
  if (p && !p.solved && last.undo) PUZZLES[p.type].undo?.(p, last.undo);
  emit(ctx, 'power.planB', { restored: last.tenths, alarm: s.alarm });
  return null;
}

export function skipOptional(ctx: Ctx, by: PlayerId): ActionError | null {
  const s = ctx.s;
  const exec = s.exec;
  if (s.phase !== 'execution' || !exec) return fail('wrongPhase', 'not now');
  if (mustPlayer(s, by).role !== 'mastermind')
    return fail('notAllowed', 'only the mastermind skips alcoves');
  for (let j = exec.stopIndex + 1; j < exec.itinerary.length; j++) {
    const st = exec.itinerary[j] as Stop;
    const prev = exec.itinerary[j - 1] as Stop;
    const startsDetour =
      isOptionalStop(st) && !(st.kind === 'room' && prev.kind === 'corridor' && prev.optional);
    if (startsDetour) {
      skipDetour(ctx, j);
      emit(ctx, 'stop.skipRequested', {});
      return null;
    }
  }
  return fail('nothingToDo', 'there is no alcove ahead to skip');
}

// ---- Time ------------------------------------------------------------------------------

/** The timer's speed in permille of real time. */
export function timerRatePermille(s: { alarm: number }, exec: ExecState): number {
  let rate = 1000;
  if (s.alarm >= BALANCE.policeAlertAt) rate = (rate * BALANCE.policeTimerRatePermille) / 1000;
  if (exec.elapsedMs < exec.rushUntilMs) rate = (rate * BALANCE.rushTimerRatePermille) / 1000;
  return rate;
}

function outcomeOfTick(r: TickOk): Outcome {
  return { ...r, fixedTenths: 0, undo: r.errorTenths > 0 ? { kind: 'none' } : null };
}

export function execStep(ctx: Ctx, ms: number): void {
  const s = ctx.s;
  const exec = s.exec;
  if (!exec || (s.phase !== 'execution' && s.phase !== 'escape')) return;
  exec.elapsedMs += ms;
  exec.timerUs -= ms * timerRatePermille(s, exec);

  // Tunnels on the night train.
  const map = getMap(s.config.mapId);
  if (map.tunnels && exec.nextTunnelAtMs >= 0 && exec.elapsedMs >= exec.nextTunnelAtMs) {
    exec.tunnelUntilMs = exec.elapsedMs + BALANCE.tunnelMs;
    exec.nextTunnelAtMs = exec.elapsedMs + BALANCE.tunnelEveryMs;
    emit(ctx, 'tunnel', { ms: BALANCE.tunnelMs });
  }

  // Cameras that are still active slowly raise the alarm (GDD §6.3).
  const watching = activePuzzles(s).some((p) => p.type === 'cameras' && !p.solved);
  if (watching && !isLooped(exec) && !isTunnel(exec)) {
    exec.dripAccMs += ms;
    while (exec.dripAccMs >= BALANCE.cameraDripEveryMs) {
      exec.dripAccMs -= BALANCE.cameraDripEveryMs;
      addAlarm(ctx, BALANCE.fixed.cameraDrip, false, 'drip');
    }
  }
  if (heistOver(s)) return;

  processTimed(ctx);
  if (heistOver(s)) return;

  for (const p of activePuzzles(s)) {
    if (p.solved) continue;
    const r = PUZZLES[p.type].tick?.(p, ms, tickContext(s));
    if (!r) continue;
    settle(ctx, p, outcomeOfTick(r));
    if (heistOver(s)) return;
  }

  if (exec.timerUs <= 0) failHeist(ctx, 'time');
}

export { failHeist };
